import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import { encodeAbiParameters, keccak256, parseAbi, stringToHex, toHex,
  type Address, type Hex, type PublicClient } from 'viem';
import { z } from 'zod';
import { terminalRouterVersionForDecoder } from './terminalRouterVersion.js';

type SupportedChainId = 4663 | 46630;
const UINT256_MAX = (1n << 256n) - 1n;
const addressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/)
  .refine(value => !/^0x0{40}$/i.test(value)).transform(value => value.toLowerCase() as Address);
const hashSchema = z.string().regex(/^0x[0-9a-fA-F]{64}$/)
  .refine(value => !/^0x0{64}$/i.test(value)).transform(value => value.toLowerCase() as Hex);
const uint256Schema = z.bigint().min(0n).max(UINT256_MAX);
const pinSchema = z.strictObject({ account: addressSchema, codeHash: hashSchema });
export const directNativeWrapperSourceFamily = 'solmate-weth-8d910d87-solc0826-o3000-ir-cancun-v1' as const;
const profileSchemaForChain = <ChainId extends SupportedChainId>(chainId: ChainId) => z.strictObject({
  schemaVersion: z.literal(1),
  profile: z.literal('reviewed-solmate-direct-weth-v1'),
  sourceFamily: z.literal(directNativeWrapperSourceFamily),
  chainId: z.literal(chainId),
  requiredDecoderVersion: z.literal('5.12.0'),
  routerVersion: z.literal('2.1.2'),
  router: pinSchema,
  wrapper: pinSchema,
});
const sourceSchema = z.strictObject({ number: uint256Schema, hash: hashSchema, timestamp: uint256Schema });
const policySchema = z.strictObject({
  maxAgeSeconds: z.number().int().safe().min(1),
  maxFutureSeconds: z.number().int().safe().min(0),
});
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

export type DirectNativeWrapperObservationProfile<ChainId extends SupportedChainId = typeof SELECTED_ACCOUNT_CHAIN_ID> =
  z.infer<ReturnType<typeof profileSchemaForChain<ChainId>>>;
export type DirectNativeWrapperObservationClient = Pick<PublicClient,
  'getBytecode' | 'readContract' | 'getBalance' | 'getBlock' | 'getChainId'>;
export const directNativeWrapperObservationAbi = parseAbi([
  'function decimals() view returns (uint8)',
  'function totalSupply() view returns (uint256)',
]);

// Frozen unmodified Solmate commit 8d910d876f51c3b2585c9109409d601f600e68e1.
// output/testnet46630-direct-wrapper-candidate-20261009-8d910d87 retains full
// source/AST/input/compiler/output/layout. Input SHA256:
// 507c94b027bbde8b454957722ab07d141e655f80dc73b0d200cba83909a4dec9.
// solc 0.8.26+8a97fa7a, optimizer3000, viaIR, Cancun, literal metadata/hash none.
// This hash covers EVERY template byte, including metadata, with only the
// compiler's three zero immutable placeholders. No caller chooses a template,
// offset, mask, domain, decimals or compiler family; no live address/hash pin.
const wrapperRuntimeBytes = 3264;
const wrapperTemplateHash = '0x7eee9d9180d5b4397bdaa682a67a1b3c0e9db5819f230aacf926a8297ba7333d';
const wrapperImmutableOffsets = [
  { name: 'decimals', offset: 1545 }, // AST24
  { name: 'INITIAL_CHAIN_ID', offset: 2751 }, // AST38
  { name: 'INITIAL_DOMAIN_SEPARATOR', offset: 2789 }, // AST40
] as const;
// UR2.1.2 retained compiler output SHA256:
// da3d8ef51074fdb9be00509d461ea6ee8fe10a814aa3965f3812daacb661fb36.
// Its source AST2379 is WETH9; these are ALL of its immutable references.
const weth9Offsets = [6749, 12200, 12536] as const;
type WrapperImmutableReference = {
  name: typeof wrapperImmutableOffsets[number]['name']; offset: number; length: 32; word: Hex;
};

/** Shape/source-family selection is not independent provenance review or
 * deployment admission. The caller must review the exact deployed code pins,
 * constructor facts and router tuple. No live addresses or permissions default.
 * Explicit domain selection does not configure a client, activate mainnet or
 * translate an existing profile. */
export function readSelectedDirectNativeWrapperObservationProfile<ChainId extends SupportedChainId>(
  input: unknown, chainId: ChainId,
): DirectNativeWrapperObservationProfile<ChainId> {
  if (chainId !== 4663 && chainId !== 46630) throw new Error('Direct native wrapper profile unsupported chain');
  const profile = profileSchemaForChain(chainId).parse(structuredClone(input));
  if (same(profile.router.account, profile.wrapper.account)) {
    throw new Error('Direct native wrapper profile role collision');
  }
  if ([profile.router, profile.wrapper].some(pin => same(pin.codeHash, keccak256('0x')))) {
    throw new Error('Direct native wrapper profile requires nonempty runtime');
  }
  return profile;
}

export function readDirectNativeWrapperObservationProfile(input: unknown): DirectNativeWrapperObservationProfile {
  return readSelectedDirectNativeWrapperObservationProfile(input, SELECTED_ACCOUNT_CHAIN_ID);
}

export function assertDirectNativeWrapperRouterCompatibility(
  profile: DirectNativeWrapperObservationProfile<SupportedChainId>, requiredDecoderVersion: string,
): void {
  if (profile.schemaVersion !== 1 || profile.profile !== 'reviewed-solmate-direct-weth-v1'
    || profile.sourceFamily !== directNativeWrapperSourceFamily || profile.requiredDecoderVersion !== '5.12.0'
    || profile.routerVersion !== '2.1.2' || requiredDecoderVersion !== profile.requiredDecoderVersion
    || terminalRouterVersionForDecoder(requiredDecoderVersion) !== profile.routerVersion) {
    throw new Error('Direct native wrapper profile router version mismatch');
  }
}

/** Source-owned version association only; not permission to execute. */
export function directNativeWrapperRouterWeth9Offsets(
  profile: DirectNativeWrapperObservationProfile<SupportedChainId>, requiredDecoderVersion: string,
): readonly number[] {
  assertDirectNativeWrapperRouterCompatibility(profile, requiredDecoderVersion);
  return Object.freeze([...weth9Offsets]);
}

/** Same source/constructor proof for canonical observations and transaction
 * prestate. Synchronous, inert and never deployment or accounting admission. */
export function assertDirectNativeWrapperRuntime(code: Hex,
  profile: Pick<DirectNativeWrapperObservationProfile<SupportedChainId>, 'chainId' | 'wrapper'>) {
  const domain = keccak256(encodeAbiParameters([
    { type: 'bytes32' }, { type: 'bytes32' }, { type: 'bytes32' }, { type: 'uint256' }, { type: 'address' },
  ], [keccak256(stringToHex('EIP712Domain(string name,string version,uint256 chainId,address verifyingContract)')),
    keccak256(stringToHex('Wrapped Ether')), keccak256(stringToHex('1')),
    BigInt(profile.chainId), profile.wrapper.account]));
  const expected = { decimals: toHex(18n, { size: 32 }),
    INITIAL_CHAIN_ID: toHex(BigInt(profile.chainId), { size: 32 }), INITIAL_DOMAIN_SEPARATOR: domain };
  if (code.length !== 2 + wrapperRuntimeBytes * 2) throw new Error('Direct native wrapper source runtime mismatch');
  let template = code;
  const references: WrapperImmutableReference[] = wrapperImmutableOffsets.map(({ name, offset }) => {
    const start = 2 + offset * 2;
    const word = `0x${code.slice(start, start + 64)}` as Hex;
    if (!same(word, expected[name])) throw new Error('Direct native wrapper constructor immutable mismatch');
    template = `${template.slice(0, start)}${'0'.repeat(64)}${template.slice(start + 64)}` as Hex;
    return { name, offset, length: 32, word };
  });
  if (!same(keccak256(template), wrapperTemplateHash)) throw new Error('Direct native wrapper source runtime mismatch');
  return references;
}

export type DirectNativeWrapperObservation<ChainId extends SupportedChainId = typeof SELECTED_ACCOUNT_CHAIN_ID> = {
  schemaVersion: 1;
  kind: 'direct-native-wrapper-observation';
  chainId: ChainId;
  profile: DirectNativeWrapperObservationProfile<ChainId>;
  source: { number: bigint; hash: Hex; timestamp: bigint };
  requiredDeadlineUnixSeconds: bigint;
  observedAtUnixSeconds: bigint;
  routerWeth9: Address;
  weth9ImmutableReferences: { offset: number; length: 32; word: Hex }[];
  wrapperImmutableReferences: WrapperImmutableReference[];
  decimals: 18;
  totalSupply: bigint;
  nativeCollateral: bigint;
  nativeSurplus: bigint;
  executionAuthorized: false;
  submissionAuthorized: false;
  accountingAuthorized: false;
};
type ObservationInput<ChainId extends SupportedChainId> = {
  client: DirectNativeWrapperObservationClient;
  profile: DirectNativeWrapperObservationProfile<ChainId>;
  source: { number: bigint; hash: Hex; timestamp: bigint };
  policy: { maxAgeSeconds: number; maxFutureSeconds: number };
  requiredDeadlineUnixSeconds: bigint;
  now?: () => number;
};

/** Read-only canonical prerequisite, not deposit/withdraw execution, backing
 * permanence, finality, accounting or permission. Use the existing bounded
 * createCanonicalBlockReadClient for EIP-1898 requireCanonical state reads;
 * numbered/latest state reads or overrides are not a substitute. Its caller
 * retains the shared request/transport budget and final verifySource duty.
 * The explicit reviewed source/chain and freshness are checked before/after.
 * Zero supply is valid; donations may create surplus, not trade proceeds. */
export async function observeSelectedDirectNativeWrapperAtBlock<ChainId extends SupportedChainId>(
  input: ObservationInput<SupportedChainId>, chainId: ChainId,
): Promise<DirectNativeWrapperObservation<ChainId>> {
  // Capture methods and all reviewed inputs before the first await. A later
  // caller mutation cannot renew the original domain, policy or deadline.
  const client = input.client;
  const getBytecode = client.getBytecode.bind(client);
  const readContract = client.readContract.bind(client);
  const getBalance = client.getBalance.bind(client);
  const getBlock = client.getBlock.bind(client);
  const getChainId = client.getChainId.bind(client);
  const now = input.now ?? Date.now;
  const profile = readSelectedDirectNativeWrapperObservationProfile(input.profile, chainId);
  const source = sourceSchema.parse(structuredClone(input.source));
  const policy = policySchema.parse(structuredClone(input.policy));
  const deadline = uint256Schema.min(1n).parse(input.requiredDeadlineUnixSeconds);
  let lastTime = -1;
  function fresh() {
    const time = now();
    if (!Number.isSafeInteger(time) || time < 0 || time < lastTime) {
      throw new Error('Direct native wrapper observation invalid clock');
    }
    lastTime = time;
    const seconds = BigInt(Math.floor(time / 1000));
    if (seconds >= deadline) throw new Error('Direct native wrapper observation review expired');
    if (seconds > source.timestamp + BigInt(policy.maxAgeSeconds)
      || source.timestamp > seconds + BigInt(policy.maxFutureSeconds)) {
      throw new Error('Direct native wrapper observation stale or future source');
    }
    return seconds;
  }
  async function checked<T>(read: () => Promise<T>): Promise<T> {
    fresh();
    let result: T;
    try { result = await read(); }
    catch { throw new Error('Direct native wrapper observation read failed'); }
    fresh();
    return result;
  }
  const headerSchema = z.object({ number: uint256Schema, hash: hashSchema,
    parentHash: hashSchema, timestamp: uint256Schema });
  async function header() {
    const parsed = headerSchema.parse(await checked(() => getBlock({ blockNumber: source.number, includeTransactions: false })));
    if (parsed.number !== source.number || !same(parsed.hash, source.hash) || parsed.timestamp !== source.timestamp) {
      throw new Error('Direct native wrapper observation source mismatch');
    }
    return parsed;
  }
  async function chain() {
    if (await checked(() => getChainId()) !== profile.chainId) throw new Error('Direct native wrapper observation chain mismatch');
  }
  async function runtime(pin: { account: Address; codeHash: Hex }) {
    const code = await checked(() => getBytecode({ address: pin.account, blockNumber: source.number })) ?? '0x';
    if (!/^0x(?:[0-9a-fA-F]{2})+$/.test(code) || !same(keccak256(code), pin.codeHash)) {
      throw new Error('Direct native wrapper observation runtime mismatch');
    }
    return code;
  }

  fresh();
  await chain();
  const initialHeader = await header();
  const routerCode = await runtime(profile.router);
  const expectedWord = `0x${'0'.repeat(24)}${profile.wrapper.account.slice(2)}` as Hex;
  const weth9ImmutableReferences = directNativeWrapperRouterWeth9Offsets(profile, profile.requiredDecoderVersion).map(offset => {
    const start = 2 + offset * 2;
    const word = `0x${routerCode.slice(start, start + 64)}` as Hex;
    if (routerCode.slice(start - 2, start).toLowerCase() !== '7f' || !same(word, expectedWord)) {
      throw new Error('Direct native wrapper observation router WETH9 association mismatch');
    }
    return { offset, length: 32 as const, word };
  });
  const wrapperImmutableReferences = assertDirectNativeWrapperRuntime(await runtime(profile.wrapper), profile);
  const decimals = await checked(() => readContract({ address: profile.wrapper.account,
    abi: directNativeWrapperObservationAbi, functionName: 'decimals', blockNumber: source.number }));
  if (decimals !== 18) throw new Error('Direct native wrapper observation decimals mismatch');
  const totalSupply = uint256Schema.parse(await checked(() => readContract({ address: profile.wrapper.account,
    abi: directNativeWrapperObservationAbi, functionName: 'totalSupply', blockNumber: source.number })));
  const nativeCollateral = uint256Schema.parse(await checked(() => getBalance({
    address: profile.wrapper.account, blockNumber: source.number,
  })));
  if (nativeCollateral < totalSupply) throw new Error('Direct native wrapper observation underbacked supply');
  const finalHeader = await header();
  if (!same(finalHeader.parentHash, initialHeader.parentHash)) throw new Error('Direct native wrapper observation source parent mismatch');
  await chain();
  const observedAtUnixSeconds = fresh();
  return { schemaVersion: 1, kind: 'direct-native-wrapper-observation', chainId: profile.chainId, profile, source,
    requiredDeadlineUnixSeconds: deadline, observedAtUnixSeconds, routerWeth9: profile.wrapper.account,
    weth9ImmutableReferences, wrapperImmutableReferences, decimals: 18, totalSupply, nativeCollateral,
    nativeSurplus: nativeCollateral - totalSupply, executionAuthorized: false,
    submissionAuthorized: false, accountingAuthorized: false };
}

export async function observeDirectNativeWrapperAtBlock(
  input: ObservationInput<SupportedChainId>,
): Promise<DirectNativeWrapperObservation> {
  return observeSelectedDirectNativeWrapperAtBlock(input, SELECTED_ACCOUNT_CHAIN_ID);
}
